import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router';
import Cookies from 'js-cookie';
import IconPath from '../../assets/images/warn.png';

import {
  Card,
  ContainerCards,
  ContainerMultipleContact,
  DisableTileComponent,
  HeaderComponent,
  IconWrap,
  StyleIcons,
  Text,
  TextSpan,
  TextWrap,
  TileComponent,
} from './multiple-contact.styles';
import { postMultipleContactId, CheckContactValid } from 'helpers/utilities';
import { Auth } from 'components/context/AuthContext';
import { ReactHtmlParser } from 'helpers/utilities.js';
import Popup from '../popup/index';
import LoadingSpinner from 'components/_common/loading-spinner';
import { COOKIES } from 'constants/cookieNames';

const MultipleContact = ({ header, align }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { multipleContacData, userToken } = useContext(Auth);
  const [showPopup, setShowPopup] = useState(false);
  const [showOK, setShowOk] = useState(true);
  const [popupTitle, setPopupTitle] = useState('');
  const [popupContent, setPopupContent] = useState('');
  const [firstPopup, setFirstPopup] = useState(true);
  const [closeBtnText, setCloseBtnText] = useState('No');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tempMultipleContacData, setTempMultipleContacData] = useState([]);

  const hideSidebars = () => {
    const notificationsSidebar = document.querySelector('#NotificationsSideBar');
    const sideBarPane = document.querySelector('#SideBarPane');
    if (notificationsSidebar) notificationsSidebar.style.display = 'none';
    if (sideBarPane) sideBarPane.style.display = 'none';
  };

  const showSidebars = () => {
    const notificationsSidebar = document.querySelector('#NotificationsSideBar');
    const sideBarPane = document.querySelector('#SideBarPane');
    if (notificationsSidebar) notificationsSidebar.style.display = '';
    if (sideBarPane) sideBarPane.style.display = '';
  };

  const funMultipleContact = async (user) => {
    if (user.langFolder) {
      Cookies.set(COOKIES.CURRENT_LANG, user.langFolder.split('-').pop());
    }
    Cookies.set(COOKIES.ACTIVE_USER_NAME, user.linkedAccount.accountNumber);
    setCurrentUser(user);
    const isFirstLogin = Cookies.get(COOKIES.IS_FIRST_LOGIN);
    if (isFirstLogin === 'false') {
      setShowPopup(true);
      setPopupTitle('CLOSE ALL SESSIONS');
      setPopupContent('Have you closed all open sessions or applications in the partner portal?');
    } else {
      Cookies.set(COOKIES.IS_FIRST_LOGIN, 'false');
      setLoading(true);
      try {
        await postMultipleContactId(user.id, userToken);
        showSidebars();
        navigate('/', { replace: true });
      } catch (error) {
        console.warn('Error selecting contact in multiple-contact, staying on page:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // If contact already selected, redirect to dashboard
    const existingContact = Cookies.get(COOKIES.ACTIVE_USER_NAME);
    const dashboardLink = Cookies.get(COOKIES.DASHBOARD);
    const currentPath = location.pathname;

    // Only redirect if we're actually on the multiple-contact page
    if (currentPath === '/multiple-contact' && existingContact && dashboardLink) {
      navigate(dashboardLink, { replace: true });
      return;
    }

    // Only hide sidebars if we're on the multiple-contact page
    if (currentPath === '/multiple-contact') {
      hideSidebars();
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (!multipleContacData.length || !userToken) return;

    // Only validate contacts if we're actually on the multiple-contact page
    const currentPath = location.pathname;
    if (currentPath !== '/multiple-contact') {
      return;
    }

    setLoading(true);

    const validateContacts = async () => {
      const validationPromises = multipleContacData.map(async (contact) => {
        const validatedContact = {
          ...contact,
          city: '',
          postalCode: '',
          disabled: true,
        };

        try {
          const contactResponse = await CheckContactValid(contact.id, userToken);
          const contactData = contactResponse?.data;

          validatedContact.city = contactData.city;
          validatedContact.postalCode = contactData.postalCode;
          validatedContact.disabled = !contactData?.isValid;
        } catch (err) {
          console.warn('Error validating contact:', err);
        }

        return validatedContact;
      });

      const validatedList = await Promise.all(validationPromises);
      setTempMultipleContacData(validatedList);
      setLoading(false);
    };

    validateContacts();
  }, [multipleContacData, userToken, location.pathname]);

  const handlePopupNo = () => {
    if (firstPopup === true) {
      setFirstPopup(false);
      setShowOk(false);
      setPopupContent('Please close all other sessions and try again');
      setCloseBtnText('Close');
    } else {
      setFirstPopup(true);
      setShowPopup(false);
      setShowOk(true);
      setPopupContent('Have you closed all open sessions or applications in the partner portal?');
      setCloseBtnText('NO');
    }
  };

  const handlePopupYes = async () => {
    setShowPopup(false);
    // Cookies.remove(COOKIES.IS_FIRST_LOGIN);
    setLoading(true);
    try {
      await postMultipleContactId(currentUser.id, userToken);
      showSidebars();
      navigate('/', { replace: true });
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ContainerMultipleContact>
        <HeaderComponent align={align}>{header}</HeaderComponent>
        <ContainerCards>
          {tempMultipleContacData.map((item) => {
            return item.disabled ? (
              <DisableTileComponent key={item.id + 1}>
                <Card>
                  <IconWrap>
                    <StyleIcons name="Building" />
                  </IconWrap>
                  <TextWrap>
                    <Text>{ReactHtmlParser(item.linkedAccount.companyName)}</Text>
                    <TextSpan>{ReactHtmlParser(item.linkedAccount.accountAddress1Country)}</TextSpan>
                    <TextSpan>{ReactHtmlParser(item.linkedAccount.accountNumber)}</TextSpan>

                    <TextSpan>
                      {ReactHtmlParser(item.city)} - {ReactHtmlParser(item.postalCode)}
                    </TextSpan>
                  </TextWrap>
                </Card>
              </DisableTileComponent>
            ) : (
              <TileComponent onClick={() => funMultipleContact(item)} key={item.id}>
                <Card>
                  <IconWrap>
                    <StyleIcons name="Building" />
                  </IconWrap>
                  <TextWrap>
                    <Text>{ReactHtmlParser(item.linkedAccount.companyName)}</Text>
                    <TextSpan>{ReactHtmlParser(item.linkedAccount.accountAddress1Country)}</TextSpan>
                    <TextSpan>{ReactHtmlParser(item.linkedAccount.accountNumber)}</TextSpan>

                    <TextSpan>
                      {ReactHtmlParser(item.city)} - {ReactHtmlParser(item.postalCode)}
                    </TextSpan>
                  </TextWrap>
                </Card>
              </TileComponent>
            );
          })}
        </ContainerCards>
      </ContainerMultipleContact>

      {/* Close All Sessions Popup Start */}
      <Popup
        handleYes={handlePopupYes}
        handleNo={handlePopupNo}
        show={showPopup}
        headerText={popupTitle}
        detailText={popupContent}
        showOkButton={showOK}
        closeBtnText={closeBtnText}
        iconUrl={IconPath}
      />
      {/* Close All Sessions Popup End  */}

      {/* Start Loader */}
      {loading ? <LoadingSpinner /> : null}
      {/* End Loader */}
    </>
  );
};
MultipleContact.propTypes = {
  header: PropTypes.string,
  align: PropTypes.string,
};
export default MultipleContact;
